/**
 * Level Manager
 * Handles level loading, progression, and mission objectives
 */

import { THREE } from "../config/ThreeConfig.js";
import { getSpeakerName, getStageOneDebriefSteps, getStageOneLaunchSteps } from "../narrative/stageOneScript.js";

export class LevelManager {
  constructor(game) {
    this.game = game;
    this.currentLevel = null;
    this.currentLevelIndex = 0;
    this.levels = [];

    // Mission objective state (Star Fox-style route unlocks via objective success).
    this.objectiveState = null;
    this._lastRouteOutcome = null; // { success:boolean, nextIndex:number, nextName:string }

    // Spawn + completion tracking (prevents instant-complete before delayed spawns)
    this.pendingSpawns = 0;
    this.spawnTimers = new Set();
    this.isTransitioning = false;
  }
  
  async init() {
    // Define all levels
    this.levels = [
      {
        name: 'Delivery Route 66',
        type: 'stage1',
        description: 'Protect civilian freighters and eliminate hostile command presence.',
        enemies: 64,
        boss: 'marketmakeromega',
        objective: { id: "protect_freighter", description: "Protect the civilian freighter (no rams, no losses)" },
        routes: { success: 2, fail: 1 },
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.78 },
          { type: 'acquisition', weight: 0.22 },
        ],
      },
      {
        name: 'Shipping Lane Raid',
        type: 'corridor',
        description: 'Compliance swarms + Acquisition escorts. Protect the lane.',
        enemies: 50,
        boss: 'shippingboss',
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.78 },
          { type: 'acquisition', weight: 0.22 },
        ],
      },
      {
        name: 'Volatility Zone',
        type: 'corridor',
        description: 'Mixed fleets, mines, fast swarms. Expect anomalies.',
        enemies: 60,
        boss: 'volatilityboss',
        objective: { id: "survive_anomaly", description: "Survive the anomaly phase" },
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.65 },
          { type: 'acquisition', weight: 0.25 },
          { type: 'compoundinterest', weight: 0.10 },
        ],
      },
      {
        name: 'Hostile Market',
        type: 'corridor',
        description: 'Elite interceptors, dense formations, ambush waves.',
        enemies: 70,
        boss: 'hostileboss',
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.55 },
          { type: 'acquisition', weight: 0.30 },
          { type: 'creativedestruction', weight: 0.15 },
        ],
      },
      {
        name: 'The Ledger Core',
        type: 'corridor',
        description: 'Survival gauntlet. All fleet classes deployed.',
        enemies: 85,
        boss: 'ledgerboss',
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.45 },
          { type: 'acquisition', weight: 0.30 },
          { type: 'compoundinterest', weight: 0.15 },
          { type: 'creativedestruction', weight: 0.10 },
        ],
      },
      {
        name: 'Liquidation Event',
        type: 'allrange',
        description: 'Endless pressure. Everything gets liquidated.',
        enemies: 120,
        boss: 'liquidationboss',
        enemyRoster: [
          { type: 'complianceinterceptor', weight: 0.38 },
          { type: 'acquisition', weight: 0.28 },
          { type: 'compoundinterest', weight: 0.14 },
          { type: 'creativedestruction', weight: 0.12 },
          { type: 'liquidationengine', weight: 0.08 },
        ],
      },
    ];
    
    return Promise.resolve();
  }
  
  async loadLevel(levelIndex) {
    if (levelIndex >= this.levels.length) {
      this.game.victory();
      return;
    }

    this.clearScheduledSpawns();
    this.isTransitioning = false;
    this.objectiveState = null;
    this._lastRouteOutcome = null;
    
    this.currentLevel = this.levels[levelIndex];
    this.currentLevelIndex = levelIndex;
    this.game.state.currentLevel = levelIndex;
    this.game.resetMissionStats?.(levelIndex);
    
    console.log(`Loading level ${levelIndex}: ${this.currentLevel.name}`);
    
    // Clear existing enemies
    this.game.enemyManager.clearAll();
    this.game.powerUpManager?.clearAll?.();
    this.game.friendlyManager?.clearAll?.();
    
    // Setup level based on type
    if (this.currentLevel.type === 'stage1') {
      // Stage One: launch sequence cinematic (script-aligned) before spawns begin.
      if (levelIndex === 0 && this.game?.uiManager?.showCinematicSequence) {
        try {
          await this.game.uiManager.showCinematicSequence({
            title: "LAUNCH",
            steps: getStageOneLaunchSteps().map((s) => ({
              ...s,
              speakerName: s?.speakerName || (s?.speakerId ? getSpeakerName(s.speakerId) : ""),
            })),
            skippable: true,
          });
        } catch {
          // Best-effort; never block stage start.
        }
      }
      this.setupStageOneRoute66();
    } else if (this.currentLevel.type === 'corridor') {
      this.setupCorridor();
    } else if (this.currentLevel.type === 'allrange') {
      this.setupAllRange();
    }
    
    // Show level intro
    this.game.uiManager.showMessage(`${this.currentLevel.name.toUpperCase()}`);
    this.game.uiManager.showMessage(this.currentLevel.description, 'info', 2000);
    
    return Promise.resolve();
  }

  pickEnemyType() {
    const roster = this.currentLevel?.enemyRoster;
    if (!Array.isArray(roster) || roster.length === 0) {
      return Math.random() > 0.7 ? 'bomber' : 'fighter';
    }

    const total = roster.reduce((sum, r) => sum + (Number(r?.weight) || 0), 0);
    if (total <= 0) return String(roster[0]?.type || 'fighter');

    let roll = Math.random() * total;
    for (const entry of roster) {
      const w = Number(entry?.weight) || 0;
      if (w <= 0) continue;
      roll -= w;
      if (roll <= 0) return String(entry.type);
    }
    return String(roster[roster.length - 1]?.type || 'fighter');
  }

  _makeFormationPositions(pattern, count, center) {
    const cx = Number(center?.x) || 0;
    const cy = Number(center?.y) || 0;
    const cz = Number(center?.z) || -500;

    const positions = [];
    const n = Math.max(1, Math.floor(Number(count) || 1));
    const gapX = 42;
    const gapY = 18;
    const depth = 55;

    const p = String(pattern || "v").toLowerCase();
    if (p === "line") {
      const mid = (n - 1) / 2;
      for (let i = 0; i < n; i++) {
        const dx = (i - mid) * gapX;
        const dz = -Math.abs(i - mid) * (depth * 0.45);
        positions.push(new THREE.Vector3(cx + dx, cy, cz + dz));
      }
      return positions;
    }

    if (p === "diamond") {
      const steps = [
        [0, 0, 0],
        [-1, 0.5, -0.8],
        [1, 0.5, -0.8],
        [0, -0.3, -1.6],
      ];
      for (let i = 0; i < n; i++) {
        const s = steps[i % steps.length];
        positions.push(new THREE.Vector3(cx + s[0] * gapX, cy + s[1] * gapY, cz + s[2] * depth));
      }
      return positions;
    }

    // Default: V formation (leader + alternating left/right)
    positions.push(new THREE.Vector3(cx, cy, cz));
    for (let i = 1; i < n; i++) {
      const k = Math.ceil(i / 2);
      const sign = i % 2 === 1 ? -1 : 1;
      positions.push(
        new THREE.Vector3(
          cx + sign * k * gapX,
          cy + (k * gapY) * (sign * 0.08),
          cz - k * depth
        )
      );
    }
    return positions;
  }

  _makeEntryPlan(start, end, { side = 1, duration = 1.35 } = {}) {
    const s = start.clone();
    const e = end.clone();
    const sd = Math.sign(side) || 1;

    // Cubic bezier with a lateral swoop (reads like choreography, not homing AI).
    const p0 = s;
    const p1 = s.clone().add(new THREE.Vector3(-sd * 120, -60, 160));
    const p2 = e.clone().add(new THREE.Vector3(sd * 80, 30, -90));
    const p3 = e;

    return { t: 0, duration, p0, p1, p2, p3, done: false };
  }

  _spawnChoreoEnemy(type, endPos, { side = null, duration = null } = {}) {
    const end = endPos?.isVector3 ? endPos.clone() : new THREE.Vector3(0, 0, -500);
    const s = side == null ? (Math.random() < 0.5 ? -1 : 1) : side;
    const start = new THREE.Vector3(
      end.x + s * (240 + Math.random() * 80),
      end.y + 70 + Math.random() * 45,
      end.z - (260 + Math.random() * 150)
    );

    const em = this.game?.enemyManager;
    if (!em || typeof em.spawnEnemy !== "function") return null;

    const enemy = em.spawnEnemy(type, start);
    if (!enemy) return null;

    enemy._flightPlan = this._makeEntryPlan(start, end, {
      side: s,
      duration: Number(duration) || (1.25 + Math.random() * 0.45),
    });
    return enemy;
  }
  
  setupStageOneRoute66() {
    // Scripted Stage One pacing (matches the official script through Stage One).
    const dm = this.game.dialogueManager;

    // EARLY ENCOUNTER: compliance interceptors
    this.scheduleSpawn(() => {
      dm?.speak?.('admiralbulldog', 'Enemy fighters incoming!');
      dm?.speak?.('honeybadger', 'Mine.');
      dm?.speak?.('elephant', 'Shared mission, technically.');
      this.game.uiManager?.showMessage?.('TRAINING: MOVE WITH WASD / AIM WITH MOUSE / SPACE TO FIRE', 'info', 3800);

      const positions = [
        { x: -80, y: 20, z: -260 },
        { x: 0, y: -10, z: -300 },
        { x: 80, y: 10, z: -330 },
        { x: -40, y: 50, z: -370 },
        { x: 40, y: -45, z: -410 },
        { x: 0, y: 35, z: -460 },
      ];
      for (const p of positions) {
        // Spawn off-screen and swoop into position (choreographed entry).
        this._spawnChoreoEnemy('complianceinterceptor', new THREE.Vector3(p.x, p.y, p.z), {
          side: Math.sign(p.x) || (Math.random() < 0.5 ? -1 : 1),
          duration: 1.25,
        });
      }
    }, 1200);

    // MID STAGE: civilian freighter under attack + bonus objective
    this.scheduleSpawn(() => {
      dm?.speak?.('civilianpilot', "We’re losing cargo pods!");
      dm?.speak?.('ant', 'Unacceptable.');
      this.game.uiManager?.showMessage?.('BONUS OBJECTIVE: PROTECT FREIGHTER', 'info', 2400);

      // Spawn the civilian freighter and start objective tracking.
      this.objectiveState = {
        id: "protect_freighter",
        expectedFreighters: 1,
        escorted: 0,
        destroyed: 0,
        rammed: 0,
        hits: 0,
        active: 0,
        startedAtMs:
          (globalThis.performance && typeof globalThis.performance.now === "function")
            ? globalThis.performance.now()
            : Date.now(),
      };
      const fm = this.game?.friendlyManager;
      if (fm && typeof fm.spawnFreighter === "function") {
        fm.spawnFreighter(new THREE.Vector3(0, 10, -900), { speed: 32, maxHealth: 160 });
      }

      for (let i = 0; i < 6; i++) {
        const x = (Math.random() - 0.5) * 240;
        const y = (Math.random() - 0.5) * 140;
        const z = -640 - Math.random() * 140;
        this._spawnChoreoEnemy('complianceinterceptor', new THREE.Vector3(x, y, z), { duration: 1.35 });
      }
    }, 13000);

    // SECOND WAVE: acquisition heavy cruiser enters
    this.scheduleSpawn(() => {
      dm?.speak?.('acquisitioncaptain', 'I am authorized to purchase your surrender.');
      dm?.speak?.('octopus', 'Amateur offer.');

      const heavies = [
        { x: -90, y: 0, z: -820 },
        { x: 90, y: 10, z: -860 },
      ];
      for (const p of heavies) {
        this._spawnChoreoEnemy('acquisition', new THREE.Vector3(p.x, p.y, p.z), {
          side: Math.sign(p.x) || 1,
          duration: 1.6,
        });
      }
    }, 23000);

    // OMEGA MINIONS: liquidation engines (must be cleared before the boss).
    this.scheduleSpawn(() => {
      dm?.speak?.('admiralbulldog', 'New contact class detected: LIQUIDATION ENGINES.');
      dm?.speak?.('elephant', 'Those are Omega’s asset-recovery units.');
      this.game.uiManager?.showMessage?.('INCOMING: LIQUIDATION ENGINES', 'warning', 2400);

      const engines = [
        { x: -120, y: 18, z: -980 },
        { x: 120, y: -6, z: -1010 },
        { x: -60, y: 42, z: -1040 },
        { x: 60, y: -36, z: -1070 },
      ];
      for (const p of engines) {
        this._spawnChoreoEnemy('liquidationengine', new THREE.Vector3(p.x, p.y, p.z), {
          side: Math.sign(p.x) || (Math.random() < 0.5 ? -1 : 1),
          duration: 1.55,
        });
      }

      // Escort screen (keeps pressure up while the engines arrive).
      for (let i = 0; i < 6; i++) {
        const x = (Math.random() - 0.5) * 260;
        const y = (Math.random() - 0.5) * 150;
        const z = -900 - Math.random() * 240;
        this._spawnChoreoEnemy('complianceinterceptor', new THREE.Vector3(x, y, z), { duration: 1.35 });
      }
    }, 30500);

    // EXTEND STAGE: one more mixed wave so we don't hit Omega too fast.
    this.scheduleSpawn(() => {
      dm?.speak?.('octopus', 'I have identified the source of the volatility.');
      dm?.speak?.('honeybadger', 'Is it something I can crash into?');

      const types = ['complianceinterceptor', 'acquisition', 'complianceinterceptor', 'complianceinterceptor'];
      for (let i = 0; i < 10; i++) {
        const type = types[i % types.length];
        const x = (Math.random() - 0.5) * 280;
        const y = (Math.random() - 0.5) * 160;
        const z = -1080 - Math.random() * 260;
        this._spawnChoreoEnemy(type, new THREE.Vector3(x, y, z), { duration: 1.3 + (type === 'acquisition' ? 0.25 : 0) });
      }
    }, 44500);

    // PRE-BOSS QUIET
    this.scheduleSpawn(() => {
      dm?.speak?.('elephant', 'New mass signature detected.');
      dm?.speak?.('honeybadger', 'Big one.');
      this.game.uiManager?.showMessage?.('ALERT: MASS SIGNATURE', 'warning', 2400);
      this.game.sceneManager?.startBoardroomGateSetPiece?.({
        label: 'HOSTILE BOARDROOM GATE',
        duration: 14,
      });
    }, 62000);

    // BOSS: MARKET MAKER OMEGA
    if (this.currentLevel?.boss) {
      this.scheduleSpawn(() => this.spawnBoss(this.currentLevel.boss), 74000);
    }
  }
  
  setupCorridor() {
    // Create enemy waves
    const totalEnemies = this.currentLevel.enemies;
    const waves = 7;
    const enemiesPerWave = Math.ceil(totalEnemies / waves);
    const waveIntervalMs = 6500;
    
    for (let wave = 0; wave < waves; wave++) {
      this.scheduleSpawn(() => {
        const remaining = Math.max(0, totalEnemies - wave * enemiesPerWave);
        if (remaining <= 0) return;
        const count = Math.max(1, Math.min(enemiesPerWave, remaining));
        const pattern = ["v", "line", "diamond", "v", "line", "v", "diamond"][wave % 7];
        const centerZ = -540 - wave * 220;
        const ends = this._makeFormationPositions(pattern, count, { x: 0, y: 10, z: centerZ });

        for (let i = 0; i < ends.length; i++) {
          const end = ends[i];
          const type = this.pickEnemyType();
          const side = (pattern === "line") ? (i < ends.length / 2 ? -1 : 1) : (Math.random() < 0.5 ? -1 : 1);
          this._spawnChoreoEnemy(type, end, { side, duration: 1.25 + (wave % 2) * 0.15 });
        }
      }, wave * waveIntervalMs);
    }
    
    // Spawn boss after all waves
    this.scheduleSpawn(() => {
      if (this.currentLevel.boss) {
        this.spawnBoss(this.currentLevel.boss);
      }
    }, waves * waveIntervalMs + 5000);
  }
  
  setupAllRange() {
    // Arena-style combat
    const arenaSize = 500;
    const totalEnemies = this.currentLevel.enemies;
    
    for (let i = 0; i < totalEnemies; i++) {
      this.scheduleSpawn(() => {
        const x = (Math.random() - 0.5) * arenaSize;
        const y = (Math.random() - 0.5) * arenaSize;
        const z = (Math.random() - 0.5) * arenaSize;
        
        const type = this.pickEnemyType();
        this.game.enemyManager.spawnEnemy(type, new THREE.Vector3(x, y, z));
      }, i * 500);
    }
    
    // Spawn final boss
    this.scheduleSpawn(() => {
      if (this.currentLevel.boss) {
        this.spawnBoss(this.currentLevel.boss);
      }
    }, totalEnemies * 500 + 10000);
  }
  
  async spawnBoss(bossType) {
    const bossPositions = {
      interceptorcaptain: new THREE.Vector3(0, 50, -900),
      shippingboss: new THREE.Vector3(0, 80, -1100),
      volatilityboss: new THREE.Vector3(0, 40, -1100),
      hostileboss: new THREE.Vector3(0, 70, -1200),
      ledgerboss: new THREE.Vector3(0, 100, -1300),
      liquidationboss: new THREE.Vector3(0, 120, -1400),
      marketmakeromega: new THREE.Vector3(0, 60, -1100),
    };
    
    const position = bossPositions[bossType] || new THREE.Vector3(0, 0, -1000);
    await this.game.enemyManager.spawnBoss(bossType, position);
    
    this.game.uiManager.showMessage('WARNING: BOSS DETECTED', 'warning', 3000);
  }
  
  shouldSpawnBoss() {
    return this.currentLevel && this.currentLevel.boss !== null;
  }
  
  completeLevel() {
    this.levelComplete();
  }
  
  levelComplete() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    this.game.addScore(1000);
    this.game.addEquity(10);

    let nextIndex = this.game.state.currentLevel + 1;
    const routes = this.currentLevel?.routes;
    let routeOutcome = null;
    let stageResult = null;
    if (routes && typeof routes === "object") {
      const success = this.isObjectiveSuccess();
      nextIndex = success ? Number(routes.success) : Number(routes.fail);
      if (!Number.isFinite(nextIndex)) nextIndex = this.game.state.currentLevel + 1;
      const nextName = this.levels?.[nextIndex]?.name || `Stage ${nextIndex + 1}`;
      stageResult = this.calculateStageResult({ objectiveSuccess: success, nextIndex, nextName });
      routeOutcome = { success, nextIndex, nextName, stageResult };
      this._lastRouteOutcome = routeOutcome;
      try {
        this.game.uiManager?.showMessage?.(
          `ROUTE ${success ? "UNLOCKED" : "DEFAULT"}: ${String(nextName).toUpperCase()}`,
          success ? "success" : "info",
          2200
        );
      } catch {
        // ignore
      }
    }
    setTimeout(() => {
      (async () => {
        try {
          // Stage One: play the official debrief scene before moving on.
          if (this.currentLevelIndex === 0 && this.game?.uiManager?.showCinematicSequence) {
            await this.game.uiManager.showCinematicSequence({
              title: "POST-MISSION DEBRIEF",
              steps: getStageOneDebriefSteps({
                objectiveSuccess: routeOutcome?.success || false,
                civiliansRammed: Number(this.objectiveState?.rammed) || 0,
                routeName: routeOutcome?.nextName || null,
                ...routeOutcome?.stageResult,
              }),
              skippable: true,
            });
          }
        } finally {
          this.loadLevel(nextIndex);
        }
      })();
    }, 1200);
  }

  isObjectiveSuccess() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return false;
    // Success = freighter not destroyed + not rammed, and either escorted out or still alive at stage end.
    const destroyed = Number(st.destroyed) || 0;
    const rammed = Number(st.rammed) || 0;
    const escorted = Number(st.escorted) || 0;
    const active = Number(st.active) || 0;
    return destroyed === 0 && rammed === 0 && (escorted >= 1 || active >= 1);
  }

  calculateStageResult({ objectiveSuccess = false, nextIndex = null, nextName = null } = {}) {
    const st = this.game?.missionStats || {};
    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    const elapsedMs = Math.max(0, now - (Number(st.startedAtMs) || now));
    const kills = Math.max(0, Number(st.kills) || 0);
    const bosses = Math.max(0, Number(st.bosses) || 0);
    const damageTaken = Math.max(0, Math.round(Number(st.damageTaken) || 0));
    const bombsUsed = Math.max(0, Number(st.bombsUsed) || 0);
    const scoreDelta = Math.max(0, (Number(this.game?.state?.score) || 0) - (Number(st.scoreAtStart) || 0));

    let rank = "C";
    if (objectiveSuccess && kills >= 44 && damageTaken <= 60 && bosses >= 1) rank = "S";
    else if (objectiveSuccess && kills >= 34 && damageTaken <= 110) rank = "A";
    else if (kills >= 24) rank = "B";

    const medal =
      rank === "S"
        ? "BOARDROOM ACE"
        : rank === "A"
          ? "HOSTILE TAKEOVER PREVENTED"
          : rank === "B"
            ? "CONTRACT FULFILLED"
            : "SURVIVED THE AUDIT";

    return {
      rank,
      medal,
      kills,
      bosses,
      damageTaken,
      bombsUsed,
      scoreDelta,
      elapsedMs,
      objectiveSuccess: !!objectiveSuccess,
      nextIndex,
      nextName,
    };
  }

  onFreighterSpawned() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return;
    st.active = Math.max(0, Number(st.active) || 0) + 1;
  }

  onFreighterEscorted() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return;
    st.escorted = Math.max(0, Number(st.escorted) || 0) + 1;
    st.active = Math.max(0, (Number(st.active) || 0) - 1);
    this.game.uiManager?.showMessage?.("OBJECTIVE UPDATE: FREIGHTER ESCORTED", "success", 1800);
  }

  onFreighterDestroyed() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return;
    st.destroyed = Math.max(0, Number(st.destroyed) || 0) + 1;
    st.active = Math.max(0, (Number(st.active) || 0) - 1);
    this.game.uiManager?.showMessage?.("OBJECTIVE FAILED: FREIGHTER LOST", "warning", 2400);
  }

  onCivilianHit() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return;
    st.hits = Math.max(0, Number(st.hits) || 0) + 1;
  }

  onCivilianRammed() {
    const st = this.objectiveState;
    if (!st || st.id !== "protect_freighter") return;
    st.rammed = Math.max(0, Number(st.rammed) || 0) + 1;
    this.game.uiManager?.showMessage?.("OBJECTIVE FAILED: CIVILIAN RAMMED", "warning", 1800);
  }

  scheduleSpawn(fn, delayMs) {
    this.pendingSpawns++;
    const timerId = setTimeout(() => {
      this.spawnTimers.delete(timerId);
      try {
        fn();
      } finally {
        this.pendingSpawns = Math.max(0, this.pendingSpawns - 1);
      }
    }, delayMs);
    this.spawnTimers.add(timerId);
    return timerId;
  }

  clearScheduledSpawns() {
    for (const timerId of this.spawnTimers) {
      clearTimeout(timerId);
    }
    this.spawnTimers.clear();
    this.pendingSpawns = 0;
  }

  maybeCompleteLevel(enemiesAlive) {
    if (this.isTransitioning) return;
    if (!this.currentLevel) return;
    if (this.pendingSpawns > 0) return;
    if (enemiesAlive > 0) return;

    this.levelComplete();
  }
  
  dispose() {
    this.clearScheduledSpawns();
    this.currentLevel = null;
  }
}
